import { contractSuretyTypeToLabelMap } from "@inrev/common";
import { bondFormFileUrlQueryKey } from "@portal/api/bond";
import type { AdminSuretyBond } from "@portal/api/bond/admin";
import { useBondFollowers } from "@portal/api/followers";
import { cn } from "@portal/components/lib/utils";
import { Button } from "@portal/components/primitives";
import { type Tab, TabBar } from "@portal/components/ui/TabBar";
import { useColumnWidth, useColumnWidthSource } from "@portal/hooks/useColumnWidth";
import { useIsCommercialSuretyEnabled } from "@portal/hooks/useIsCommercialSuretyEnabled";
import {
	type ReportScrolledFromTop,
	useScrolledFromTopGestures,
} from "@portal/providers/ScrolledFromTopReporterProvider";
import { type FC, useCallback, useRef, type WheelEvent } from "react";
import { useGetFileUrl } from "../../../../api";
import type { NonEmptyArray } from "../../../../shared/types";
import { type BondTabId, useBondTabNavigation } from "../../../shared/bond/useBondTabNavigation";
import { AdminWorkflowLayout } from "../../layout/AdminWorkflowLayout";
import { AdminTools } from "../../shared/tools/AdminTools";
import { BondCard } from "../components/AdminBondCard";
import { AdminCloseBondModal } from "../components/AdminCloseBondModal";
import { AdminBondStickyPanel } from "./AdminBondStickyPanel";
import { AdminBondFormTab } from "./tabs/AdminBondFormTab";
import { AdminBondOverviewTab } from "./tabs/AdminBondOverviewTab";
import { AdminBondPeriodsTab } from "./tabs/AdminBondPeriodsTab";
import type { BondPageBodyProps } from "./types";

export type AdminBondPageLayoutProps = {
	bond: AdminSuretyBond;
};

const ENTITY_VIEW_GUTTER_CLASS = "mx-24";

const BOND_TABS: NonEmptyArray<Tab<BondPageBodyProps> & { id: BondTabId }> = [
	{ id: "overview", label: "Overview", Component: AdminBondOverviewTab },
	{ id: "bond-form", label: "Bond Form", Component: AdminBondFormTab },
	{
		id: "periods-and-adjustments",
		label: "Periods & Adjustments",
		Component: AdminBondPeriodsTab,
	},
];

export const AdminBondPageLayout: FC<AdminBondPageLayoutProps> = ({ bond }) => {
	const followersControl = useBondFollowers(bond.id, true);
	const { getFileUrl, fileUrlLoading } = useGetFileUrl();
	const isCommercialSuretyEnabled = useIsCommercialSuretyEnabled();
	const { activeTabId, setActiveTabId, headerCollapsed, setHeaderCollapsed, getHeaderCollapsed } =
		useBondTabNavigation({ tabsEnabled: isCommercialSuretyEnabled });
	const { Component: TabContent } = BOND_TABS.find((tab) => tab.id === activeTabId) ?? BOND_TABS[0];

	const scrollRef = useRef<HTMLDivElement | null>(null);
	const column = useColumnWidthSource();
	const compactTabs = useColumnWidth(column, (width) => width !== undefined && width < 850);
	const reportScrolledFromTop = useCallback<ReportScrolledFromTop>(
		(scrolledFromTop) => setHeaderCollapsed(scrolledFromTop),
		[setHeaderCollapsed],
	);

	const {
		onScroll: handlePageScroll,
		attachNativeWheel: attachPageWheel,
		decideWheel: decideChromeWheel,
	} = useScrolledFromTopGestures(reportScrolledFromTop, undefined, getHeaderCollapsed);

	const nativeWheelDisposerRef = useRef<(() => void) | undefined>(undefined);
	const setScrollRef = useCallback(
		(node: HTMLDivElement | null) => {
			scrollRef.current = node;
			nativeWheelDisposerRef.current?.();
			nativeWheelDisposerRef.current = node ? attachPageWheel(node) : undefined;
		},
		[attachPageWheel],
	);

	const handleChromeWheel = (e: WheelEvent<HTMLDivElement>) => {
		const target = scrollRef.current;
		if (!target || (e.target instanceof Node && target.contains(e.target))) return;
		const decision = decideChromeWheel(target, e);
		if (decision.collapse) {
			reportScrolledFromTop(true);
			return;
		}
		if (decision.block) return;
		if (decision.expand) reportScrolledFromTop(false);
		target.scrollBy({ top: e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY });
	};

	const handleTabChange = useCallback(
		(tabId: string) => {
			setActiveTabId(tabId);
			scrollRef.current?.scrollTo?.({ top: 0, behavior: "instant" });
		},
		[setActiveTabId],
	);

	const actionButtons = (
		<div className="flex w-full flex-col gap-2">
			<Button
				color="primary"
				className="w-full"
				onClick={() =>
					getFileUrl({
						baseUrl: `/v2/admin/surety/bonds/${bond.id}/bond-form`,
						queryKey: bondFormFileUrlQueryKey(bond.id, true),
						asDownload: true,
					})
				}
				loading={fileUrlLoading}
				trackingId="bond-admin-download"
			>
				Download Bond
			</Button>
			{bond.status === "open" && (
				<AdminCloseBondModal
					bond={{
						id: bond.id,
						accountId: bond.accountId,
						suretyType: bond.suretyType,
						contractSuretyType: bond.contractSuretyType,
						bondType: bond.contractSuretyType,
						amount: bond.amount,
						number: bond.number,
					}}
					button={
						<Button
							variant="outline"
							color="secondary"
							className="w-full"
							trackingId="bond-admin-close"
						>
							Close Bond
						</Button>
					}
				/>
			)}
		</div>
	);

	return (
		<AdminWorkflowLayout
			title={`${contractSuretyTypeToLabelMap[bond.contractSuretyType]} Bond ${bond.number}`}
			contentClassName="bg-surface-light"
			toolbar={
				<AdminTools
					relation={{ relationType: "bond", relationId: bond.id }}
					defaultUnderwriterId={bond.underwriterUserId}
				/>
			}
		>
			<div
				className="w-full h-full flex flex-col min-h-0"
				onWheel={handleChromeWheel}
				data-testid="admin-bond-entity-view-layout"
				ref={column.ref}
			>
				<div
					data-testid="admin-bond-header-wrapper"
					className={cn(
						"shrink-0 grid bg-surface-base transition-[grid-template-rows] duration-300 ease-in-out motion-reduce:transition-none",
						headerCollapsed ? "grid-rows-[0fr]" : "grid-rows-[1fr]",
					)}
				>
					<div className="overflow-hidden min-h-0">
						<div
							className={cn(
								ENTITY_VIEW_GUTTER_CLASS,
								"flex flex-col space-y-[16px] pt-[18px]",
								!isCommercialSuretyEnabled && "pb-[18px]",
							)}
						>
							<BondCard
								bond={bond}
								actionButtons={actionButtons}
								followersControl={followersControl}
							/>
						</div>
					</div>
				</div>

				<div
					data-testid={headerCollapsed ? "admin-bond-collapsed-chrome" : undefined}
					className={cn(
						"relative bg-surface-base shrink-0 z-[25] border-b border-b-bc-subtle",
						isCommercialSuretyEnabled && "shadow-md shadow-gray-50",
						headerCollapsed &&
							!isCommercialSuretyEnabled &&
							"flex items-center justify-end px-4 py-2 shadow-md shadow-gray-50",
					)}
				>
					{isCommercialSuretyEnabled && (
						<div className={cn(ENTITY_VIEW_GUTTER_CLASS, "flex")}>
							<TabBar
								options={BOND_TABS}
								activeTabId={activeTabId}
								onTabChange={handleTabChange}
								className="pt-4 gap-8"
								minimal
								compact={compactTabs}
								trackingPrefix="bond-admin-tab"
							/>
						</div>
					)}
					{headerCollapsed ? (
						<div
							data-testid="admin-bond-collapsed-summary"
							className={
								isCommercialSuretyEnabled
									? "absolute right-8 top-1/2 -translate-y-1/2 hidden xs:block"
									: undefined
							}
						>
							<AdminBondStickyPanel
								bond={bond}
								column={column}
								alwaysShow={!isCommercialSuretyEnabled}
							/>
						</div>
					) : null}
				</div>

				<div
					data-testid="admin-bond-scroll-container"
					ref={setScrollRef}
					className="flex-1 min-h-0 overflow-auto overscroll-y-contain"
					onScroll={handlePageScroll}
				>
					<div className={cn(ENTITY_VIEW_GUTTER_CLASS, "flex flex-col mt-6 pb-[125px]")}>
						<TabContent
							bond={bond}
							followersControl={followersControl}
							commercialSuretyEnabled={isCommercialSuretyEnabled}
						/>
					</div>
				</div>
			</div>
		</AdminWorkflowLayout>
	);
};
